import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  concatMap,
  debounceTime,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  DIRECT_SHIPPING_ACTIONS,
  inspiredPicksSelectors,
  INSPIRED_PICKS_ACTIONS,
  MY_ORDERS_ACTIONS,
  NEXT_DELIVERY_ACTIONS,
  CART_ACTIONS,
  VISUAL_PUNCHTHRU_ACTIONS,
  OrderItemDTOService,
  OrderDtoService,
  Order,
  OrderStatus,
  SHIP_TO_ACTIONS,
  DSM_ACTIONS,
} from '@usf/ngrx-order';
import { ORDER_TRACING_ACTIONS } from './order-tracing.actions';
import { OrderTracingMessages } from './order-tracing.constants';
import { cancelOrderTrace } from './trace-models/cancel-order.trace';
import { getOrderDetailTrace } from './trace-models/get-order-detail.trace';
import { getRecentOrdersTrace } from './trace-models/get-recent-orders.trace';
import { Store } from '@ngrx/store';
import { SharedTrackingService } from 'src/app/shared/services/analytics/shared-tracking.service';
import { combineLatest, of, zip } from 'rxjs';
import { PanAppState, Tracking } from '@panamax/app-state';
import { genericTracingTransformer } from '@shared/tracking/tracing/generic-tracing-transformer';
import { nextDeliveryDateCallTrace } from './trace-models/next-delivery-date.trace';
import { getCustomerOrdersTrace } from './trace-models/get-customer-orders.trace';
import { OrderTracingService } from './order-tracing.service';
import { ORDER_ACTIONS } from '@order/actions/order-actions';
import { calculatePrice } from '@shared/selectors/helpers/order-total-price.selectors.helper';
import { OrderService } from '@order/services/order.service';
import { getVptOrderTrace } from './trace-models/get-vpt-order.trace';
import { submitVptOrderTrace } from './trace-models/submit-vpt-order.trace';
import { selectProductAttributesByIds } from '@shared/selectors/product.selectors';
import { cancelSubmittedOrderTrace } from './trace-models/cancel-submitted-order.trace';
import {
  shipToConversionTrace,
  shipToConversionWebsocketTrace,
} from './trace-models/ship-to-conversion.trace';
import { OrderConversionService } from '@order/services/order-conversion.service';
import {
  dsmCustomersCallEndTrace,
  dsmCustomersCallStartTrace,
} from './trace-models/dsm-customers-call.trace';

@Injectable({
  providedIn: 'root',
})
export class OrderTracingEffects {
  submitCart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CART_ACTIONS.submitCart),
        mergeMap(data => {
          return combineLatest([
            of(data),
            this.orderService.getProductsForTracing$(data?.order?.orderItems),
          ]).pipe(take(1));
        }),
        tap(([data, productMap]) => {
          const tracingProducts = SharedTrackingService.tracingProductData(
            data?.order?.orderItems,
            productMap,
          );
          return this.orderTracingService.trackSubmitOrderApiResponse(
            data?.order?.orderHeader,
            tracingProducts,
          );
        }),
      ),
    { dispatch: false },
  );

  getOrderDetailTmUserApiCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.getOrderDetails),
      map(payload => {
        let trace = JSON.parse(JSON.stringify(getOrderDetailTrace));
        trace.tracing.data.isEndOfTrace = false;
        trace.tracing.data.isStartOfTrace = true;
        trace.tracing.data.attributes.order.tandemOrderNumber =
          payload.tandemOrderNumber;
        trace.tracing.transformFunc = genericTracingTransformer;
        return trace;
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceGetOrderDetail({ tracking }),
      ]),
    ),
  );
  getOrderDetailTmUserApiCallSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.getOrderDetailsSuccess),
      map(payload => {
        let trace = JSON.parse(JSON.stringify(getOrderDetailTrace));
        trace.tracing.data.isEndOfTrace = true;
        trace.tracing.data.isStartOfTrace = false;
        trace.tracing.data.attributes.order.orderId =
          payload.order.orderHeader.orderId;
        trace.tracing.data.attributes.tandemOrderNumber =
          payload.order.orderHeader.tandemOrderNumber;
        trace.tracing.transformFunc = genericTracingTransformer;
        return trace;
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceGetOrderDetailSuccess({ tracking }),
      ]),
    ),
  );

  getOrderDetailTmUserApiCallFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.getOrderDetailsFailure),
      map(payload => {
        let trace = JSON.parse(JSON.stringify(getOrderDetailTrace));
        trace.tracing.data.isEndOfTrace = true;
        trace.tracing.data.isStartOfTrace = false;
        trace.tracing.data.attributes.order.errorDetails =
          payload.error || payload['message'];
        trace.tracing.transformFunc = genericTracingTransformer;
        return trace;
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceGetOrderDetailFail({ tracking }),
      ]),
    ),
  );

  getInspiredPicksApiCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(INSPIRED_PICKS_ACTIONS.getInspiredPicks),
      withLatestFrom(
        this.store.select(inspiredPicksSelectors.selectInspiredPicks),
      ),
      switchMap(_ => [
        ORDER_TRACING_ACTIONS.traceInspiredPicksCall({
          tracking: {
            tracing: {
              data: {
                traceContext: OrderTracingMessages.capability,
                isEndOfTrace: false,
                isStartOfTrace: true,
                attributes: {
                  event: OrderTracingMessages.inspiredPicksCallEvent,
                },
              },
              transformFunc: genericTracingTransformer,
            },
          },
        }),
      ]),
    ),
  );

  getInspiredPicksApiCallSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(INSPIRED_PICKS_ACTIONS.getInspiredPicksSuccess),
      withLatestFrom(
        this.store.select(inspiredPicksSelectors.selectInspiredPicks),
      ),
      switchMap(payload => [
        ORDER_TRACING_ACTIONS.traceInspiredPicksCallSuccess({
          tracking: {
            tracing: {
              data: {
                traceContext: OrderTracingMessages.capability,
                isEndOfTrace: true,
                isStartOfTrace: false,
                attributes: {
                  event: OrderTracingMessages.inspiredPicksCallEvent,
                  inspiredPicksProductCount:
                    payload[0]?.inspiredPicks?.productNumberList?.length,
                },
              },
              transformFunc: genericTracingTransformer,
            },
          },
        }),
      ]),
    ),
  );

  getInspiredPicksApiCallFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(INSPIRED_PICKS_ACTIONS.getInspiredPicksFail),
      withLatestFrom(
        this.store.select(inspiredPicksSelectors.selectInspiredPicks),
      ),
      switchMap(_ => [
        ORDER_TRACING_ACTIONS.traceInspiredPicksCallFail({
          tracking: {
            tracing: {
              data: {
                traceContext: OrderTracingMessages.capability,
                isEndOfTrace: true,
                isStartOfTrace: false,
                attributes: {
                  event: OrderTracingMessages.inspiredPicksCallEvent,
                },
              },
              transformFunc: genericTracingTransformer,
            },
          },
        }),
      ]),
    ),
  );

  getDirectShippingSavingsCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DIRECT_SHIPPING_ACTIONS.getDirectShippingSavings),
      mergeMap(payload => {
        return combineLatest([
          of(payload),
          this.panAppState.user$,
          this.panAppState.customer$,
          this.orderService.getProductsForTracing$(payload?.order?.orderItems),
        ]).pipe(take(1));
      }),
      switchMap(([payload, user, customer, productMap]) => {
        return of(
          ORDER_TRACING_ACTIONS.traceDirectShippingSavingsCall({
            tracking: {
              tracing: {
                data: {
                  traceContext: OrderTracingMessages.capability,
                  isStartOfTrace: true,
                  isEndOfTrace: false,
                  attributes: {
                    event: OrderTracingMessages.getDirectShippingSavingsEvent,
                    customer: {
                      customerNumber: customer.customerNumber,
                      departmentNumber: customer.departmentNumber,
                      divisionNumber: customer.divisionNumber,
                    },
                    user: {
                      id: user.userId,
                    },
                    order: {
                      orderId: payload.order.orderHeader.orderId,
                      origOrderId: !!payload.order.orderHeader.origOrderId
                        ? payload.order.orderHeader.origOrderId
                        : '',
                      origSplitOrderId: !!payload.order.orderHeader
                        .origSplitOrderId
                        ? payload.order.orderHeader.origSplitOrderId
                        : '',
                      tandemOrderNumber: !!payload.order.orderHeader
                        .tandemOrderNumber
                        ? payload.order.orderHeader.tandemOrderNumber
                        : '',
                    },
                    products: SharedTrackingService.tracingProductData(
                      payload.order.orderItems,
                      productMap,
                    ),
                  },
                },
                transformFunc: genericTracingTransformer,
              },
            },
          }),
        );
      }),
    ),
  );

  getDirectShippingSavingsCallSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DIRECT_SHIPPING_ACTIONS.getDirectShippingSavingsSuccess),
      switchMap(payload => {
        if (!payload.directShippingSavings.directShippingSavings.errorDetails) {
          return of(
            ORDER_TRACING_ACTIONS.traceDirectShippingSavingsCallSuccess({
              tracking: {
                tracing: {
                  data: {
                    traceContext: OrderTracingMessages.capability,
                    isStartOfTrace: false,
                    isEndOfTrace: true,
                    attributes: {
                      event: OrderTracingMessages.getDirectShippingSavingsEvent,
                    },
                  },
                  transformFunc: genericTracingTransformer,
                },
              },
            }),
          );
        } else {
          return of(
            ORDER_TRACING_ACTIONS.traceDirectShippingSavingsCallFail({
              tracking: {
                tracing: {
                  data: {
                    traceContext: OrderTracingMessages.capability,
                    isStartOfTrace: false,
                    isEndOfTrace: true,
                    attributes: {
                      event: OrderTracingMessages.getDirectShippingSavingsEvent,
                      error: {
                        details:
                          payload.directShippingSavings.directShippingSavings
                            .errorDetails.errDetails,
                      },
                    },
                  },
                  transformFunc: genericTracingTransformer,
                },
              },
            }),
          );
        }
      }),
    ),
  );

  getDirectShippingSavingsCallFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DIRECT_SHIPPING_ACTIONS.getDirectShippingSavingsFail),
      switchMap(payload => {
        return of(
          ORDER_TRACING_ACTIONS.traceDirectShippingSavingsCallFail({
            tracking: {
              tracing: {
                data: {
                  traceContext: OrderTracingMessages.capability,
                  isStartOfTrace: false,
                  isEndOfTrace: true,
                  attributes: {
                    event: OrderTracingMessages.getDirectShippingSavingsEvent,
                    error: {
                      details: payload.error,
                    },
                  },
                },
                transformFunc: genericTracingTransformer,
              },
            },
          }),
        );
      }),
    ),
  );

  getNextDeliveryDateApiCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NEXT_DELIVERY_ACTIONS.getDeliveryInfo),
      switchMap(_ => [
        ORDER_TRACING_ACTIONS.nextDeliveryDateCall({
          tracking: {
            tracing: {
              data: {
                traceContext: OrderTracingMessages.nextDeliveryDateCallEvent,
                isEndOfTrace: false,
                isStartOfTrace: true,
                attributes: {
                  event: OrderTracingMessages.nextDeliveryDateCallEvent,
                },
              },
              transformFunc: genericTracingTransformer,
            },
          },
        }),
      ]),
    ),
  );

  getNextDeliveryDateApiCallSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NEXT_DELIVERY_ACTIONS.getDeliveryInfoSuccess),
      map(payload => {
        const trackingData = JSON.parse(
          JSON.stringify(nextDeliveryDateCallTrace),
        );
        trackingData.tracing.data.isStartOfTrace = false;
        trackingData.tracing.data.isEndOfTrace = true;
        trackingData.tracing.data.attributes.order.nextDeliverydate =
          payload.nextDelivery.deliveryDate;
        trackingData.tracing.data.attributes.order.errorDetails.errorNumber =
          payload?.nextDelivery?.errNumber;
        trackingData.tracing.transformFunc = genericTracingTransformer;
        return OrderTracingService.deepRemoveUndefined(trackingData);
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.nextDeliveryDateCallSuccess({ tracking }),
      ]),
    ),
  );

  getNextDeliveryDateApiCallFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NEXT_DELIVERY_ACTIONS.getDeliveryInfoFail),
      map(payload => {
        const trackingData = JSON.parse(
          JSON.stringify(nextDeliveryDateCallTrace),
        );
        trackingData.tracing.data.isStartOfTrace = false;
        trackingData.tracing.data.isEndOfTrace = true;
        trackingData.tracing.data.attributes.order.errorDetails = payload.error;
        trackingData.tracing.transformFunc = genericTracingTransformer;
        return trackingData;
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.nextDeliveryDateCallFail({ tracking }),
      ]),
    ),
  );

  traceGetRecentOrdersCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.getRecentOrders),
      withLatestFrom(this.panAppState?.user$),
      debounceTime(200),
      switchMap(([payload, user]) => {
        let trackingData = JSON.parse(JSON.stringify(getRecentOrdersTrace));
        trackingData.tracing.data.isStartOfTrace = true;
        trackingData.tracing.data.isEndOfTrace = false;
        trackingData.tracing.data.attributes.user.id = user?.userId;
        trackingData.tracing.transformFunc = genericTracingTransformer;
        return of(trackingData);
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceGetRecentOrdersCall({
          tracking: OrderTracingService.deepRemoveUndefined(tracking),
        }),
      ]),
    ),
  );

  traceGetRecentOrdersCallSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.getRecentOrdersSuccess),
      withLatestFrom(this.panAppState?.user$),
      switchMap(([payload, user]) => {
        let trackingData = JSON.parse(JSON.stringify(getRecentOrdersTrace));
        trackingData.tracing.data.isStartOfTrace = false;
        trackingData.tracing.data.isEndOfTrace = true;
        (trackingData.tracing.data.attributes.user.id = user?.userId),
          (trackingData.tracing.transformFunc = genericTracingTransformer);
        return of(trackingData);
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceGetRecentOrdersCallSuccess({
          tracking: OrderTracingService.deepRemoveUndefined(tracking),
        }),
      ]),
    ),
  );

  traceGetRecentOrdersCallFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.getRecentOrdersFail),
      withLatestFrom(this.panAppState?.user$),
      switchMap(([payload, user]) => {
        let trackingData = JSON.parse(JSON.stringify(getRecentOrdersTrace));
        trackingData.tracing.data.isStartOfTrace = false;
        trackingData.tracing.data.isEndOfTrace = true;
        (trackingData.tracing.data.attributes.user.id = user?.userId),
          (trackingData.tracing.transformFunc = genericTracingTransformer);
        return of(trackingData);
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceGetRecentOrdersCallFailure({
          tracking: OrderTracingService.deepRemoveUndefined(tracking),
        }),
      ]),
    ),
  );

  traceGetCustomerOrdersCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.getCustomerOrders),
      withLatestFrom(this.panAppState.user$),
      switchMap(([payload, user]) => {
        let trackingData = JSON.parse(JSON.stringify(getCustomerOrdersTrace));
        trackingData.tracing.data.isStartOfTrace = true;
        (trackingData.tracing.data.attributes.user.id = user?.userId),
          (trackingData.tracing.transformFunc = genericTracingTransformer);
        return of(trackingData);
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceGetCustomerOrdersCall({ tracking }),
      ]),
    ),
  );
  traceGetCustomerOrdersCallSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.getCustomerOrdersSuccess),
      withLatestFrom(this.panAppState.user$),
      switchMap(([payload, user]) => {
        let trackingData = JSON.parse(JSON.stringify(getCustomerOrdersTrace));
        trackingData.tracing.data.isStartOfTrace = false;
        trackingData.tracing.data.isEndOfTrace = true;
        (trackingData.tracing.data.attributes.user.id = user.userId),
          (trackingData.tracing.transformFunc = genericTracingTransformer);
        return of(trackingData);
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceGetCustomerOrdersCallSuccess({ tracking }),
      ]),
    ),
  );
  traceGetCustomerOrdersCallFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.getCustomerOrdersFail),
      withLatestFrom(this.panAppState.user$),
      switchMap(([payload, user]) => {
        let trackingData = JSON.parse(JSON.stringify(getCustomerOrdersTrace));
        trackingData.tracing.data.isStartOfTrace = false;
        trackingData.tracing.data.isEndOfTrace = true;
        (trackingData.tracing.data.attributes.user.id = user.userId),
          (trackingData.tracing.transformFunc = genericTracingTransformer);
        return of(trackingData);
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceGetCustomerOrdersCallFail({ tracking }),
      ]),
    ),
  );

  traceCancelSubmittedCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.cancelSubmittedCart),
      map(payload => {
        const trace = JSON.parse(JSON.stringify(cancelSubmittedOrderTrace));
        trace.tracing.data.attributes.event =
          OrderTracingMessages.cancelSubmittedOrder;
        trace.tracing.data.isEndOfTrace = false;
        trace.tracing.data.isStartOfTrace = true;
        trace.tracing.data.attributes.order.orderId =
          payload.order.orderHeader.orderId;
        trace.tracing.data.attributes.order.tandemOrderNumber =
          payload.order.orderHeader.tandemOrderNumber;
        (trace.tracing.data.attributes.order.totalAmount =
          Math.round(payload.order.orderHeader.totalDollars * 100) / 100),
          (trace.tracing.transformFunc = genericTracingTransformer);
        return trace;
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceCancelSubmittedOrder({ tracking }),
      ]),
    ),
  );

  traceCancelAddToOrderModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ORDER_ACTIONS.cancelAddToOrderModal),
      mergeMap(props => {
        return combineLatest([
          of(props),
          this.orderService.getProductsForTracing$(
            props?.modalData?.origOrderItems,
          ),
        ]).pipe(take(1));
      }),
      switchMap(([props, productMap]) => {
        const data = props?.modalData;
        const tracingProducts = SharedTrackingService.tracingProductData(
          [data?.updateOrderItem],
          productMap,
        );
        const tracking = JSON.parse(JSON.stringify(cancelOrderTrace));
        tracking.tracing.data.isEndOfTrace = true;
        tracking.tracing.data.isStartOfTrace = true;
        tracking.tracing.data.traceContext = OrderTracingMessages.capability;
        tracking.tracing.data.attributes.event =
          OrderTracingMessages.cancelAddToOrderModal;
        tracking.tracing.data.attributes.order.requestedDeliveryDate =
          data.orderHeader?.requestedDeliveryDate;
        tracking.tracing.data.attributes.order.confirmedDeliveryDate =
          data.orderHeader?.confirmedDeliveryDate;
        tracking.tracing.data.attributes.products = tracingProducts;
        tracking.tracing.transformFunc = genericTracingTransformer;

        return of(
          ORDER_TRACING_ACTIONS.traceAddToOrderModalCancel({ tracking }),
        );
      }),
    ),
  );

  traceSubmitAddToOrderModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ORDER_ACTIONS.dismissAddToOrderModal),
      mergeMap(props => {
        return combineLatest([
          of(props),
          this.orderService.getProductsForTracing$(
            props?.modalData?.origOrderItems,
          ),
        ]).pipe(take(1));
      }),
      switchMap(([props, productMap]) => {
        const data = props.modalData;
        const isNewOrder = data.currentTab === 1;
        let newPrice = isNewOrder ? 0 : data.orderHeader.totalPrice;
        const updateOrderItemDetails = productMap?.get(
          data.updateOrderItem.productNumber,
        );

        const updateQuantity =
          data.updateOrderItem?.eachesOrdered?.currentValue > 0
            ? data.updateOrderItem?.eachesOrdered?.currentValue
            : data.updateOrderItem?.unitsOrdered?.currentValue;
        newPrice =
          newPrice +
          calculatePrice(
            updateQuantity,
            updateOrderItemDetails?.pricing?.unitPrice?.toString(),
            updateOrderItemDetails?.pricing?.priceUom === 'CS',
            updateOrderItemDetails?.summary?.catchWeightFlag,
            updateOrderItemDetails?.summary?.netWeight,
            updateOrderItemDetails?.inventory?.eachConversionFactor,
          );
        const tracking = OrderTracingService.createAddToOrderTrace(
          data.orderHeader,
          [data.updateOrderItem],
          productMap,
          true,
          true,
        );
        tracking.tracing.data.attributes.order.totalAmount = newPrice;
        tracking.tracing.data.attributes.event = isNewOrder
          ? OrderTracingMessages.createOrder
          : OrderTracingMessages.editOrder;

        return isNewOrder
          ? of(
              ORDER_TRACING_ACTIONS.traceAddToOrderModalCreate({
                tracking,
              }),
            )
          : of(
              ORDER_TRACING_ACTIONS.traceAddToExistingOrderModal({
                tracking,
              }),
            );
      }),
    ),
  );

  traceGetVptOrderCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VISUAL_PUNCHTHRU_ACTIONS.getVptOrderByOrderNumber),
      mergeMap(payload => {
        return combineLatest([
          of(payload),
          this.panAppState.user$,
          this.panAppState.customer$,
        ]).pipe(
          filter(
            ([payload, user, customer]) =>
              !!user?.userId && !!customer?.customerNumber,
          ),
          take(1),
        );
      }),
      switchMap(([payload, user, customer]) => {
        let trackingData = JSON.parse(JSON.stringify(getVptOrderTrace));
        trackingData.tracing.data.isStartOfTrace = true;
        trackingData.tracing.data.attributes.order.orderId =
          payload.orderNumber;
        trackingData.tracing.data.attributes.customer.customerNumber =
          customer.customerNumber;
        trackingData.tracing.data.attributes.customer.departmentNumber =
          customer.departmentNumber;
        trackingData.tracing.data.attributes.customer.divisionNumber =
          customer.divisionNumber;
        trackingData.tracing.data.attributes.user.id = user?.userId;
        trackingData.tracing.transformFunc = genericTracingTransformer;
        return of(trackingData);
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceGetVptOrderCall({ tracking }),
      ]),
    ),
  );
  traceGetVptOrderCallSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VISUAL_PUNCHTHRU_ACTIONS.getVptOrderByOrderNumberSuccess),
      mergeMap(payload => {
        return combineLatest([
          of(payload),
          this.panAppState.user$,
          this.panAppState.customer$,
        ]).pipe(
          filter(
            ([payload, user, customer]) =>
              !!user?.userId && !!customer?.customerNumber,
          ),
          take(1),
        );
      }),
      switchMap(([payload, user, customer]) => {
        let trackingData = JSON.parse(JSON.stringify(getVptOrderTrace));
        trackingData.tracing.data.isEndOfTrace = true;
        trackingData.tracing.data.attributes.order.orderId =
          payload.orderNumber;
        trackingData.tracing.data.attributes.customer.customerNumber =
          customer.customerNumber;
        trackingData.tracing.data.attributes.customer.departmentNumber =
          customer.departmentNumber;
        trackingData.tracing.data.attributes.customer.divisionNumber =
          customer.divisionNumber;
        trackingData.tracing.data.attributes.user.id = user?.userId;
        trackingData.tracing.transformFunc = genericTracingTransformer;
        return of(trackingData);
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceGetVptOrderCallSuccess({ tracking }),
      ]),
    ),
  );
  traceGetVptOrderCallFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VISUAL_PUNCHTHRU_ACTIONS.getVptOrderByOrderNumberFail),
      mergeMap(payload => {
        return combineLatest([
          of(payload),
          this.panAppState.user$,
          this.panAppState.customer$,
        ]).pipe(
          filter(
            ([payload, user, customer]) =>
              !!user?.userId && !!customer?.customerNumber,
          ),
          take(1),
        );
      }),
      switchMap(([payload, user, customer]) => {
        let trackingData = JSON.parse(JSON.stringify(getVptOrderTrace));
        trackingData.tracing.data.isEndOfTrace = true;
        trackingData.tracing.data.attributes.order.errorDetails =
          payload.error || payload['message'];
        trackingData.tracing.data.attributes.customer.customerNumber =
          customer.customerNumber;
        trackingData.tracing.data.attributes.customer.departmentNumber =
          customer.departmentNumber;
        trackingData.tracing.data.attributes.customer.divisionNumber =
          customer.divisionNumber;
        trackingData.tracing.data.attributes.user.id = user?.userId;
        trackingData.tracing.transformFunc = genericTracingTransformer;
        return of(trackingData);
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceGetVptOrderCallFail({ tracking }),
      ]),
    ),
  );

  //starts websocket tracing. Ends with traceSubmitVptOrderResponseEnd which will fire when order is in state.
  traceSubmitVpt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VISUAL_PUNCHTHRU_ACTIONS.submitVptOrder),
      switchMap(payload => {
        let trackingData = JSON.parse(JSON.stringify(submitVptOrderTrace));
        trackingData.tracing.data.isStartOfTrace = true;
        trackingData.tracing.data.attributes.order.orderId =
          payload.order.orderHeader.orderId;
        trackingData.tracing.data.attributes.order.origOrderId = !!payload.order
          .orderHeader.origOrderId
          ? payload.order.orderHeader.origOrderId
          : '';
        trackingData.tracing.data.attributes.order.origSplitOrderId = !!payload
          .order.orderHeader.origSplitOrderId
          ? payload.order.orderHeader.origSplitOrderId
          : '';
        trackingData.tracing.transformFunc = genericTracingTransformer;
        sessionStorage.setItem(
          'submitVPTOrderTrace',
          JSON.stringify([payload.order, false, false]),
        );
        return of(trackingData);
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceSubmitVptOrderStart({ tracking }),
      ]),
    ),
  );
  traceSubmitVptOrderApiResponseSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VISUAL_PUNCHTHRU_ACTIONS.submitVptOrderSuccess),
      mergeMap(payload => {
        return combineLatest([
          of(payload),
          this.orderService.getProductsForTracing$(payload?.order?.orderItems),
        ]).pipe(take(1));
      }),
      switchMap(([payload, products]) => {
        const productTracing = SharedTrackingService.tracingProductData(
          payload.order.orderItems,
          products,
        );
        let trackingData = JSON.parse(JSON.stringify(submitVptOrderTrace));
        trackingData.tracing.data.traceContext =
          OrderTracingMessages.submitVptOrderApiResponse;
        trackingData.tracing.data.attributes.order.orderId =
          payload.order.orderHeader.orderId;
        trackingData.tracing.data.attributes.order.origOrderId = !!payload.order
          .orderHeader.origOrderId
          ? payload.order.orderHeader.origOrderId
          : '';
        trackingData.tracing.data.attributes.order.origSplitOrderId = !!payload
          .order.orderHeader.origSplitOrderId
          ? payload.order.orderHeader.origSplitOrderId
          : '';
        trackingData.tracing.data.attributes.order.tandemOrderNumber = !!payload
          .order.orderHeader.tandemOrderNumber
          ? payload.order.orderHeader.tandemOrderNumber
          : '';
        trackingData.tracing.data.attributes.products = productTracing;
        trackingData.tracing.transformFunc = genericTracingTransformer;
        return of(trackingData);
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceSubmitVptOrderApiResponseSuccess({
          tracking,
        }),
      ]),
    ),
  );
  traceSubmitVptOrderApiResponseFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VISUAL_PUNCHTHRU_ACTIONS.submitVptOrderFail),
      switchMap(payload => {
        let trackingData = JSON.parse(JSON.stringify(submitVptOrderTrace));
        trackingData.tracing.data.traceContext =
          OrderTracingMessages.submitVptOrderApiResponse;
        trackingData.tracing.data.attributes.order.errorDetails = payload.error;
        trackingData.tracing.transformFunc = genericTracingTransformer;
        return of(trackingData);
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceSubmitVptOrderApiResponseFail({ tracking }),
      ]),
    ),
  );

  traceSubmitVptOrderWebsocketResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.submitIpOrderAsync),
      filter(
        webSocketResponse => webSocketResponse?.orderTransactions?.length > 0,
      ),
      concatMap(webSocketResponse => {
        const websocketMsgContentLength =
          webSocketResponse?.orderTransactions.length;

        const orderTransaction =
          websocketMsgContentLength === 1
            ? webSocketResponse?.orderTransactions[0]
            : webSocketResponse?.orderTransactions[1];

        const clientOrderItems = this.orderItemDtoService.toClientModelArray(
          orderTransaction.orderItems,
        );
        const clientOrder =
          this.orderDtoService.toClientModel(orderTransaction);
        const order = {
          orderHeader: clientOrder,
          orderItems: clientOrderItems,
        } as Order;
        const submitOrderTraceArr = JSON.parse(
          sessionStorage.getItem('submitVPTOrderTrace'),
        ) || [clientOrder, true, false, false];
        const [sessionOrder, isWebSocket, traceDispatched, isEmpty] =
          submitOrderTraceArr;
        sessionStorage.setItem(
          'submitVPTOrderTrace',
          JSON.stringify([sessionOrder, true, traceDispatched]),
        );
        return [
          ORDER_TRACING_ACTIONS.endVptOrderSubmissionTrace({
            order,
            isWebsocket: true,
          }),
        ];
      }),
    ),
  );

  traceSubmitVptOrderResponseEnd$ = createEffect(
    () =>
      zip([
        this.actions$.pipe(
          ofType(ORDER_TRACING_ACTIONS.traceSubmitVptOrderApiResponseSuccess),
        ),
        this.actions$.pipe(
          ofType(ORDER_TRACING_ACTIONS.endVptOrderSubmissionTrace),
        ),
      ]).pipe(
        tap(submittedOrderAndNewOrderArr => {
          const [submittedOrder, receivedOrder] = submittedOrderAndNewOrderArr;
          const newOrder = receivedOrder.order;
          let allProductNumbers: number[] = [];
          const submittedOrderId =
            submittedOrder?.tracking?.tracing?.data?.attributes?.order[
              'orderId'
            ];
          const submitOrderTraceArr = JSON.parse(
            sessionStorage.getItem('submitVPTOrderTrace'),
          ) || [null, true, true, false];
          const [sessionOrder, isWebSocket, traceDispatched, isEmpty] =
            submitOrderTraceArr;
          if (
            !!submittedOrderId &&
            !!sessionOrder &&
            traceDispatched === false &&
            (submittedOrderId === newOrder?.orderHeader?.origOrderId ||
              submittedOrderId === newOrder?.orderHeader?.orderId ||
              submittedOrderId === newOrder?.orderHeader?.origSplitOrderId) &&
            (sessionOrder?.orderHeader?.orderId ===
              newOrder?.orderHeader?.orderId ||
              sessionOrder?.orderHeader?.orderId ===
                newOrder?.orderHeader?.origSplitOrderId ||
              sessionOrder?.orderHeader?.orderId ===
                newOrder?.orderHeader?.origOrderId) &&
            (newOrder?.orderHeader?.orderStatus === OrderStatus.SUBMITTED ||
              newOrder?.orderHeader?.orderStatus ===
                OrderStatus.SUBMITTED_WITH_EXCEPTIONS ||
              newOrder?.orderHeader?.orderStatus === OrderStatus.ORDER_ERROR)
          ) {
            sessionStorage.setItem(
              'submitVPTOrderTrace',
              JSON.stringify([sessionOrder, isWebSocket, true]),
            );
            const incomingOrderItems = newOrder?.orderItems;
            const submittedOrderProducts =
              submittedOrder?.tracking?.tracing?.data?.attributes?.products;

            incomingOrderItems.map(item =>
              allProductNumbers.push(item?.productNumber),
            );
            allProductNumbers = Array.from(
              new Set(allProductNumbers.filter(item => item !== undefined)),
            );
            this.store
              .select(selectProductAttributesByIds(allProductNumbers))
              .pipe(
                take(1),
                map(productMap => {
                  const incomingOrderProducts =
                    SharedTrackingService.tracingProductData(
                      incomingOrderItems,
                      productMap,
                    );

                  const trackingData = {
                    tracing: {
                      data: {
                        isEndOfTrace: true,
                        isStartOfTrace: false,
                        traceContext: OrderTracingMessages.submitVptOrder,
                        attributes: {
                          event: OrderTracingMessages.vptOrder,
                          order: {
                            orderId:
                              newOrder?.orderHeader?.orderId || undefined,
                            origOrderId:
                              newOrder?.orderHeader?.origOrderId || undefined,
                            origSplitOrderId:
                              newOrder?.orderHeader?.origSplitOrderId ||
                              undefined,
                            tandemOrderNumber:
                              newOrder?.orderHeader?.tandemOrderNumber,
                            requestedDeliveryDate:
                              newOrder?.orderHeader?.requestedDeliveryDate,
                            confirmedDeliveryDate:
                              newOrder?.orderHeader?.confirmedDeliveryDate,
                            totalAmount: newOrder?.orderHeader?.totalDollars,
                            isWebsocket:
                              isWebSocket || receivedOrder.isWebsocket,
                          },
                          webSocketOrderProducts: incomingOrderProducts,
                          submittedOrderProducts: submittedOrderProducts,
                          errorDetails:
                            newOrder?.orderHeader?.errorDetails || undefined,
                        },
                      },
                    },
                  } as Tracking;

                  this.orderTracingService.trackSubmitVptOrderWebsocket(
                    trackingData,
                  );
                }),
              )
              .subscribe();
          }
        }),
      ),
    { dispatch: false },
  );

  traceShipToConversionStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SHIP_TO_ACTIONS.updateShipToDetails),
      switchMap(payload => {
        sessionStorage.setItem(
          'shipToConversionTrace',
          payload.order.orderHeader.orderId,
        );
        let trackingData = JSON.parse(
          JSON.stringify(shipToConversionWebsocketTrace),
        );
        trackingData.tracing.transformFunc = genericTracingTransformer;
        trackingData.tracing.data.isStartOfTrace = true;
        trackingData.tracing.data.isEndOfTrace = false;
        trackingData.tracing.data.attributes.order.tandemOrderNumber =
          payload.order.orderHeader.tandemOrderNumber;
        trackingData.tracing.data.attributes.order.isWebsocket = null;
        trackingData.tracing.data.attributes.order.orderId =
          payload.order.orderHeader.orderId;
        trackingData.tracing.data.attributes.order.origOrderId = !!payload.order
          .orderHeader.origOrderId
          ? payload.order.orderHeader.origOrderId
          : '';
        trackingData.tracing.data.attributes.order.origSplitOrderId = !!payload
          .order.orderHeader.origSplitOrderId
          ? payload.order.orderHeader.origSplitOrderId
          : '';
        return of(trackingData);
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceShipToConversionStart({
          tracking: OrderTracingService.deepRemoveUndefined(tracking),
        }),
      ]),
    ),
  );
  traceShipToConversionApiResponseSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SHIP_TO_ACTIONS.updateShipToDetailsSuccess),
      switchMap(payload => {
        let trackingData = JSON.parse(JSON.stringify(shipToConversionTrace));
        trackingData.tracing.data.isStartOfTrace = true;
        trackingData.tracing.data.isEndOfTrace = true;
        trackingData.tracing.data.attributes.order.tandemOrderNumber =
          payload.order.orderHeader.tandemOrderNumber;
        trackingData.tracing.data.attributes.order.orderId =
          payload.order.orderHeader.orderId;
        trackingData.tracing.data.attributes.order.origOrderId = !!payload.order
          .orderHeader.origOrderId
          ? payload.order.orderHeader.origOrderId
          : '';
        trackingData.tracing.data.attributes.order.origSplitOrderId = !!payload
          .order.orderHeader.origSplitOrderId
          ? payload.order.orderHeader.origSplitOrderId
          : '';
        trackingData.tracing.data.attributes.order.errorDetails = !!payload
          ?.order?.orderHeader?.errorDetails
          ? payload?.order?.orderHeader?.errorDetails
          : undefined;
        trackingData.tracing.transformFunc = genericTracingTransformer;

        return of(trackingData);
      }),
      switchMap(tracking => [
        ORDER_TRACING_ACTIONS.traceShipToConversionApiResponse({
          tracking: OrderTracingService.deepRemoveUndefined(tracking),
        }),
      ]),
    ),
  );

  shipToConversionWebsocketResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MY_ORDERS_ACTIONS.updateShipToDetailsAsync),
      switchMap(payload => {
        const orderItems = this.orderItemDtoService.toClientModelArray(
          payload?.orderTransactions[0].orderItems,
        );
        combineLatest([
          of(payload),
          this.orderService.getProductsForTracing$(orderItems),
        ])
          .pipe(take(1))
          .subscribe(([asyncPayload, productMap]) => {
            const orderTransaction = asyncPayload.orderTransactions[0];
            const clientOrderItems =
              this.orderItemDtoService.toClientModelArray(
                orderTransaction.orderItems,
              );
            const clientOrderHeader =
              this.orderDtoService.toClientModel(orderTransaction);
            const order: Order = {
              orderHeader: clientOrderHeader,
              orderItems: clientOrderItems,
            };
            this.orderTracingService.traceShipToConversionWebsocketResponse(
              order,
              productMap,
              true,
            );
            return [];
          });
        return [];
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store<any>,
    private panAppState: PanAppState,
    private orderTracingService: OrderTracingService,
    private orderService: OrderService,
    private orderItemDtoService: OrderItemDTOService,
    private orderDtoService: OrderDtoService,
    private orderConversionService: OrderConversionService,
  ) {}
}
